import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close,
  PictureAsPdf,
  TableChart,
  Email,
  Download,
  CheckCircle,
} from '@mui/icons-material';
import accountingService from '../../services/accountingService';

interface ExportReportDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Dialog d'export de rapport comptable avec options PDF/CSV et envoi email
 */
export const ExportReportDialog: React.FC<ExportReportDialogProps> = ({ open, onClose }) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [format, setFormat] = useState<'pdf' | 'csv'>('pdf');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (format === 'pdf') {
        await accountingService.downloadExpensePdfReport(year, month);
        setSuccess('Rapport PDF téléchargé avec succès !');
      } else {
        await accountingService.downloadExpenseCsvReport(year, month);
        setSuccess('Rapport CSV téléchargé avec succès !');
      }
    } catch (err: any) {
      console.error('Erreur lors du téléchargement:', err);
      setError(err.response?.data?.message || 'Erreur lors du téléchargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!email.trim()) {
      setError('Veuillez saisir une adresse email');
      return;
    }

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Adresse email invalide');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await accountingService.shareExpenseReportByEmail(year, month, email, format);
      setSuccess(`Rapport ${format.toUpperCase()} envoyé à ${email} avec succès !`);
      setEmail(''); // Reset email field
    } catch (err: any) {
      console.error('Erreur lors de l\'envoi par email:', err);
      setError(
        err.response?.data?.message || 'Erreur lors de l\'envoi du rapport par email'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  const getYearOptions = () => {
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) {
      years.push(i);
    }
    return years;
  };

  const getMonthOptions = () => {
    return [
      { value: 1, label: 'Janvier' },
      { value: 2, label: 'Février' },
      { value: 3, label: 'Mars' },
      { value: 4, label: 'Avril' },
      { value: 5, label: 'Mai' },
      { value: 6, label: 'Juin' },
      { value: 7, label: 'Juillet' },
      { value: 8, label: 'Août' },
      { value: 9, label: 'Septembre' },
      { value: 10, label: 'Octobre' },
      { value: 11, label: 'Novembre' },
      { value: 12, label: 'Décembre' },
    ];
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Exporter le rapport de dépenses
          </Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Alertes */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" icon={<CheckCircle />} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {/* Sélection période */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Période
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Mois</InputLabel>
                <Select
                  value={month}
                  label="Mois"
                  onChange={(e) => setMonth(Number(e.target.value))}
                  disabled={loading}
                >
                  {getMonthOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Année</InputLabel>
                <Select
                  value={year}
                  label="Année"
                  onChange={(e) => setYear(Number(e.target.value))}
                  disabled={loading}
                >
                  {getYearOptions().map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          {/* Sélection format */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Format d'export
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant={format === 'pdf' ? 'contained' : 'outlined'}
                startIcon={<PictureAsPdf />}
                onClick={() => setFormat('pdf')}
                disabled={loading}
                fullWidth
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                }}
              >
                PDF
                {format === 'pdf' && (
                  <Chip
                    label="Sélectionné"
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: 20 }}
                  />
                )}
              </Button>
              <Button
                variant={format === 'csv' ? 'contained' : 'outlined'}
                startIcon={<TableChart />}
                onClick={() => setFormat('csv')}
                disabled={loading}
                fullWidth
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                }}
              >
                CSV (Excel)
                {format === 'csv' && (
                  <Chip
                    label="Sélectionné"
                    size="small"
                    color="primary"
                    sx={{ ml: 1, height: 20 }}
                  />
                )}
              </Button>
            </Stack>
          </Paper>

          {/* Téléchargement */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Télécharger
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Téléchargez le rapport sur votre appareil
            </Typography>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Download />}
              onClick={handleDownload}
              disabled={loading}
              fullWidth
              sx={{ py: 1.5 }}
            >
              {loading ? 'Téléchargement...' : `Télécharger le ${format.toUpperCase()}`}
            </Button>
          </Paper>

          <Divider>
            <Chip label="OU" size="small" />
          </Divider>

          {/* Envoi par email */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Partager par email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Envoyez le rapport directement à votre comptable
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Email du destinataire"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="comptable@exemple.fr"
                disabled={loading}
                fullWidth
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
              <Button
                variant="outlined"
                startIcon={loading ? <CircularProgress size={20} /> : <Email />}
                onClick={handleSendEmail}
                disabled={loading || !email.trim()}
                fullWidth
                sx={{ py: 1.5 }}
              >
                {loading ? 'Envoi...' : 'Envoyer par email'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportReportDialog;
